import { Link } from 'react-router-dom';

function Idx({ num, children }) {
  return <span className="cz-section-idx"><strong>{num}</strong>{children}</span>;
}

export default function Features() {
  const modules = [
    ['I',    'Core Performance Reviews', 'Annual, mid-year, quarterly, probation, promotion, project-based, 360, upward — all templated and orchestrated.', ['PerformYard','BambooHR reviews']],
    ['II',   'Calibration + Scoring',    'IP/TP/CP weighted composite score with role-specific weights. Dynamic band distribution engine responsive to company growth.', ['Betterworks (deepened)']],
    ['III',  'Dynamic Distribution',     'X / E+ / E / E- band allocations that shift by company growth tier. Role-group comparison. Small-team guard under 15 people.', ['— first-of-kind']],
    ['IV',   'Goals & OKRs',             'Cascading Company → Department → Team → Individual. Confidence scores. Goal completion feeds the IP score directly.', ['Betterworks OKRs','Lattice Goals']],
    ['V',    'Continuous Feedback',      '1:1 agenda builder, weekly check-ins with mood tracking, SBI-prompted feedback, peer recognition.', ['15Five','Lattice Feedback']],
    ['VI',   'Engagement & Surveys',     'Annual, pulse, eNPS, onboarding, exit, manager effectiveness, DEI climate. Anonymity-preserving heatmaps.', ['Culture Amp','15Five surveys']],
    ['VII',  'PIP + IDP',                '90-day PIP lifecycle with auto-enrol on E-. Root-cause categorisation. IDPs linked to competency framework.', ['Lattice PIP','Leapsome IDPs']],
    ['VIII', 'HRIS',                     'Employee records, org chart, time-off with 20-country policy library, documents with e-signature, onboarding / offboarding workflows.', ['BambooHR','HiBob']],
    ['IX',   'Compensation Planning',    'Salary bands, merit cycles, compa-ratio tracking, band → merit-% linkage. Budget guardrails.', ['Lattice Comp','HiBob comp']],
    ['X',    'Succession & Job Board',   'Bench strength reports, skills matching, internal job postings, readiness ratings tied to 9-box placement.', ['Lattice','Leapsome career']],
    ['XI',   'Learning & IDP',           'Course library, learning paths, IDP-linked course recommendations, mandatory compliance tracking, auto-enrolment on PIPs.', ['Leapsome Learning']],
    ['XII',  'AI Intelligence',          'Attrition risk, promotion readiness, manager effectiveness, team health, comp anomaly, bias audit, calibration suggestions, review drafting.', ['Betterworks AI']],
  ];

  return (
    <>
      {/* ══ HERO ══════════════════════════════════════════════════════ */}
      <section className="cz-section" style={{ paddingTop: 72 }}>
        <div className="cz-wrap">
          <div style={{ maxWidth: 960 }}>
            <Idx num="00">Table of instruments</Idx>
            <h1 className="cz-display cz-display-xl" style={{ marginTop: 18 }}>
              Every measurement you'd expect.<br/>
              <em>And the ones you didn't.</em>
            </h1>
            <p className="cz-lede" style={{ marginTop: 24 }}>
              Twelve modules. One data model. Every feature specified in the blueprint;
              every calculation in the open; every override audited. What Lattice, Culture
              Amp, BambooHR, and Leapsome do in four separate renewals, CalibZa does in one.
            </p>
          </div>
        </div>
      </section>

      {/* ══ Module index (12 rows) ════════════════════════════════════ */}
      <section className="cz-section" style={{ paddingTop: 32 }}>
        <div className="cz-wrap">
          <p className="cz-rule-labeled">
            <span className="cz-mono-label">Fig. 02 — Module index (I through XII)</span>
          </p>

          <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {modules.map(([num, title, body, replaces], i) => (
              <li key={num} style={{
                display: 'grid',
                gridTemplateColumns: '100px minmax(0, 1.3fr) minmax(0, 1.8fr) minmax(0, 0.8fr)',
                gap: 32,
                padding: '30px 0',
                borderBottom: i === modules.length - 1 ? 'none' : '1px solid var(--rule-soft)',
                alignItems: 'start',
              }} className="mod-row">
                <div className="cz-mono-label" style={{ color: 'var(--blue)', fontSize: 14, letterSpacing: '0.12em' }}>
                  {num}
                </div>
                <h3 style={{
                  fontFamily: 'var(--ff-display)', fontVariationSettings: '"opsz" 48, "SOFT" 60, "WONK" 0',
                  fontSize: 30, fontWeight: 500, margin: 0, letterSpacing: '-0.015em', lineHeight: 1.1,
                }}>{title}</h3>
                <p style={{ color: 'var(--muted)', fontSize: 14.5, margin: 0, lineHeight: 1.65 }}>{body}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                  <span className="cz-mono-label">Replaces</span>
                  {replaces.map(r => (
                    <span key={r} style={{ fontSize: 13, color: 'var(--ink)' }}>{r}</span>
                  ))}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ══ § 01 · SIGNATURE INNOVATIONS ═══════════════════════════════ */}
      <section className="cz-section cz-section-dark">
        <div className="cz-wrap">
          <Idx num="01">Signature innovations</Idx>
          <h2 className="cz-display cz-display-lg" style={{ marginTop: 14, maxWidth: 940 }}>
            Three things <em>only</em> CalibZa does.
          </h2>

          <div className="three-col" style={{ marginTop: 64, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 28 }}>
            {/* 1 */}
            <div style={{ border: '1px solid var(--rule-dark)', padding: 28 }}>
              <p className="cz-mono-label" style={{ color: 'var(--yellow)' }}>Mechanism 01</p>
              <h3 style={{ fontFamily: 'var(--ff-display)', fontWeight: 450, fontSize: 30, lineHeight: 1.1, margin: '16px 0 18px', letterSpacing: '-0.015em', color: 'var(--cream)' }}>
                Dynamic band distribution
              </h3>
              <p style={{ color: 'var(--muted-ink)', fontSize: 14.5, lineHeight: 1.6, margin: 0 }}>
                Your X/E+/E/E- ratios automatically widen during growth and tighten
                during contraction. Calibration is never a one-size-fits-all curve.
              </p>
              <div style={{ marginTop: 26, paddingTop: 20, borderTop: '1px solid var(--rule-dark)', display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontFamily: 'var(--ff-display)', fontStyle: 'italic', fontSize: 28, color: 'var(--yellow)' }}>+35%</span>
                <span className="cz-mono-label" style={{ color: 'var(--muted-ink)' }}>top band in growth years</span>
              </div>
            </div>

            {/* 2 */}
            <div style={{ border: '1px solid var(--rule-dark)', padding: 28 }}>
              <p className="cz-mono-label" style={{ color: 'var(--yellow)' }}>Mechanism 02</p>
              <h3 style={{ fontFamily: 'var(--ff-display)', fontWeight: 450, fontSize: 30, lineHeight: 1.1, margin: '16px 0 18px', letterSpacing: '-0.015em', color: 'var(--cream)' }}>
                Anti-gaming detection
              </h3>
              <p style={{ color: 'var(--muted-ink)', fontSize: 14.5, lineHeight: 1.6, margin: 0 }}>
                Six gaming patterns are detected automatically: peer inflation, collusion
                pairs, manager favouritism, IP-to-TP anomalies, missing data exploitation,
                and repeated override patterns.
              </p>
              <div style={{ marginTop: 26, paddingTop: 20, borderTop: '1px solid var(--rule-dark)', display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontFamily: 'var(--ff-display)', fontStyle: 'italic', fontSize: 28, color: 'var(--yellow)' }}>6</span>
                <span className="cz-mono-label" style={{ color: 'var(--muted-ink)' }}>gaming patterns surfaced pre-calibration</span>
              </div>
            </div>

            {/* 3 */}
            <div style={{ border: '1px solid var(--rule-dark)', padding: 28 }}>
              <p className="cz-mono-label" style={{ color: 'var(--yellow)' }}>Mechanism 03</p>
              <h3 style={{ fontFamily: 'var(--ff-display)', fontWeight: 450, fontSize: 30, lineHeight: 1.1, margin: '16px 0 18px', letterSpacing: '-0.015em', color: 'var(--cream)' }}>
                Explainable AI
              </h3>
              <p style={{ color: 'var(--muted-ink)', fontSize: 14.5, lineHeight: 1.6, margin: 0 }}>
                Every AI recommendation shows its top 3 input signals. No black boxes.
                Managers and HR remain the final authority on every employment decision.
              </p>
              <div style={{ marginTop: 26, paddingTop: 20, borderTop: '1px solid var(--rule-dark)', display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontFamily: 'var(--ff-display)', fontStyle: 'italic', fontSize: 28, color: 'var(--yellow)' }}>100%</span>
                <span className="cz-mono-label" style={{ color: 'var(--muted-ink)' }}>of AI outputs show top-3 signals</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ § 02 · COMPARISON TABLE ═══════════════════════════════════ */}
      <section className="cz-section">
        <div className="cz-wrap">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 40, flexWrap: 'wrap' }}>
            <div>
              <Idx num="02">Side-by-side</Idx>
              <h2 className="cz-display cz-display-md" style={{ marginTop: 14 }}>
                What your stack <em>actually</em> does.
              </h2>
            </div>
            <span className="cz-mono-label">Sourced from public pricing pages · Apr 2026</span>
          </div>

          <div className="cmp-scroll" style={{ marginTop: 36, overflowX: 'auto', border: '1px solid var(--rule-soft)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 840 }}>
              <thead>
                <tr style={{ background: 'var(--cream-warm)' }}>
                  {['Capability','CalibZa','Lattice','Betterworks','Culture Amp','BambooHR'].map((h, i) => (
                    <th key={h} style={{
                      padding: '16px 18px', textAlign: i === 0 ? 'left' : 'center',
                      fontFamily: 'var(--ff-mono)', fontSize: 11, fontWeight: 500,
                      letterSpacing: '0.1em', textTransform: 'uppercase',
                      color: i === 1 ? 'var(--blue)' : 'var(--muted)',
                      borderBottom: '1px solid var(--rule-soft)',
                      background: i === 1 ? 'var(--blue-wash)' : undefined,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['Performance reviews (all 8 types)',      '●','●','●','○','◐'],
                  ['Growth-adjusted band distribution',      '●','○','○','○','○'],
                  ['Role-group calibration',                 '●','◐','●','○','○'],
                  ['Anti-gaming detection (6 patterns)',     '●','○','○','○','○'],
                  ['Review Quality Score',                   '●','○','○','○','○'],
                  ['Goals / OKRs with cascading',            '●','●','●','◐','○'],
                  ['360 + skip-level + upward',              '●','●','●','●','○'],
                  ['Engagement surveys with heatmaps',       '●','◐','○','●','○'],
                  ['PIP + IDP + competency framework',       '●','●','◐','○','○'],
                  ['HRIS (time-off, docs, onboarding)',      '●','○','○','○','●'],
                  ['Compensation planning',                  '●','◐','○','○','○'],
                  ['Succession + internal job board',        '●','◐','○','○','○'],
                  ['AI review drafting + bias detection',    '●','◐','◐','○','○'],
                  ['Bias audit report (demographic parity)', '●','○','○','○','○'],
                  ['Manager Effectiveness Score',            '●','○','○','○','○'],
                  ['Real payroll engine (all 50 + DC)',      '●','○','○','○','○'],
                  ['Direct-deposit NACHA ACH generation',    '●','○','○','○','○'],
                  ['Industry benchmarking (opt-in, anon.)',  '●','○','○','●','○'],
                  ['Workforce planning AI',                  '●','○','○','○','○'],
                  ['Price per employee / month',             '$14','$11–24','$22+','$15+','$10–16'],
                  ['Renewals to manage',                     '1','2–3','2–3','2–3','2–3'],
                ].map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--rule-soft)' }}>
                    {row.map((cell, ci) => (
                      <td key={ci} style={{
                        padding: '13px 18px',
                        textAlign: ci === 0 ? 'left' : 'center',
                        fontSize: ci === 0 ? 14 : 15,
                        color: ci === 1 ? 'var(--ink)' : 'var(--ink)',
                        background: ci === 1 ? 'rgba(37,99,235,0.05)' : undefined,
                        fontWeight: ci === 0 ? 500 : 600,
                        fontFamily: ci === 0 ? 'var(--ff-body)' : 'var(--ff-display)',
                        fontVariationSettings: ci > 0 ? '"opsz" 48, "SOFT" 60' : undefined,
                      }}>
                        {cell === '●' ? <span style={{ color: ci === 1 ? 'var(--blue)' : 'var(--ink)' }}>●</span> :
                         cell === '◐' ? <span style={{ color: 'var(--muted)' }}>◐</span> :
                         cell === '○' ? <span style={{ color: 'var(--rule)' }}>○</span> :
                         cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 14, display: 'flex', gap: 22, flexWrap: 'wrap' }}>
            <span className="cz-mono-label">● Full · ◐ Partial · ○ Not offered</span>
          </div>
        </div>
      </section>

      {/* ══ § 03 · THE DATA MODEL ═════════════════════════════════════ */}
      <section className="cz-section" style={{ background: 'var(--cream-warm)', borderTop: '1px solid var(--rule-soft)', borderBottom: '1px solid var(--rule-soft)' }}>
        <div className="cz-wrap">
          <div className="two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
            <div>
              <Idx num="03">The unfair advantage</Idx>
              <h2 className="cz-display cz-display-md" style={{ marginTop: 14 }}>
                One data model means intelligence <em>compounds</em>.
              </h2>
              <p className="cz-body cz-body-muted" style={{ marginTop: 20 }}>
                An attrition-risk prediction that only sees review scores will miss the
                signal. CalibZa's layer reads review scores, engagement-survey dips,
                compensation-lag against the band, 1:1 frequency, and the latest peer
                review — together.
              </p>
              <p className="cz-body cz-body-muted" style={{ marginTop: 14 }}>
                Lattice and BambooHR can't do this. Their tools share a renewal cycle,
                not a schema.
              </p>
            </div>

            <div style={{ border: '1px solid var(--rule-soft)', background: 'var(--cream)', padding: 36 }}>
              <div className="cz-fig" style={{ marginBottom: 18 }}>
                <span className="cz-fig-num">Fig. 03</span>
                <span>Inputs into a single attrition-risk score</span>
              </div>
              <div className="cz-blueprint" style={{ fontSize: 12.5 }}>
                {[
                  ['Rating trend',          '30%'],
                  ['Engagement dip',        '20%'],
                  ['Compensation lag',      '20%'],
                  ['1:1 frequency',         '15%'],
                  ['Tenure window',         '15%'],
                ].map(([k, v]) => (
                  <div key={k} className="row" style={{ padding: '10px 0', borderBottom: '1px solid var(--rule-soft)' }}>
                    <span className="dot"></span>{k}<div className="bar"/><span className="val">{v}</span>
                  </div>
                ))}
              </div>
              <p className="cz-mono-label" style={{ marginTop: 18, color: 'var(--blue)' }}>
                Every prediction surfaces its top 3 contributing signals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ CTA ═══════════════════════════════════════════════════════ */}
      <section className="cz-section" style={{ paddingTop: 96, paddingBottom: 96, textAlign: 'center' }}>
        <div className="cz-wrap">
          <h2 className="cz-display cz-display-lg" style={{ maxWidth: 900, marginInline: 'auto' }}>
            See it measure your team<br/>
            <em>before</em> the next review cycle.
          </h2>
          <div style={{ display: 'flex', gap: 12, marginTop: 34, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/register" className="cz-btn cz-btn-blue cz-btn-lg">Start free pilot <span className="cz-btn-arrow">→</span></Link>
            <Link to="/pricing" className="cz-btn cz-btn-outline cz-btn-lg">See pricing</Link>
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 900px) {
          .two-col, .three-col { grid-template-columns: 1fr !important; gap: 32px !important; }
          .mod-row { grid-template-columns: 60px 1fr !important; gap: 12px !important; }
          .mod-row > *:nth-child(3), .mod-row > *:nth-child(4) { grid-column: 2 !important; align-items: flex-start !important; }
        }
      `}</style>
    </>
  );
}
